// Dictation: record a clip, send it to /api/ai/transcribe, hand back the text.
// Native records m4a with expo-av; web uses MediaRecorder (webm). The reply
// side (speaking back) is useAiVoice().speak — the self-hosted Kokoro voice.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { Audio } from 'expo-av';
import { supabase } from '../lib/supabase';

const API = process.env.EXPO_PUBLIC_API_BASE_URL;

async function transcribe(body: FormData): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  // No Content-Type: fetch sets the multipart boundary itself.
  const res = await fetch(`${API}/api/ai/transcribe`, { method: 'POST', body, headers: token ? { Authorization: `Bearer ${token}` } : {} });
  const json = (await res.json().catch(() => ({}))) as { text?: string; error?: string };
  if (!res.ok) throw new Error(json.error || `Transcribe ${res.status}`);
  return (json.text ?? '').trim();
}

export function useDictation(onText: (text: string) => void) {
  const [state, setState] = useState<'idle' | 'recording' | 'transcribing'>('idle');
  const [error, setError] = useState<string | null>(null);
  const rec = useRef<Audio.Recording | null>(null);
  const web = useRef<{ mr: MediaRecorder; chunks: Blob[]; stream: MediaStream } | null>(null);

  // Bumped by every start() and by cancel(); work from an older generation is discarded.
  const gen = useRef(0);

  const release = useCallback(async () => {
    const w = web.current;
    web.current = null;
    if (w) {
      try {
        w.mr.onstop = null;
        if (w.mr.state !== 'inactive') w.mr.stop();
      } catch {
        // already stopped
      }
      w.stream.getTracks().forEach((t) => t.stop());
    }
    const r = rec.current;
    rec.current = null;
    if (r) {
      try {
        await r.stopAndUnloadAsync();
      } catch {
        // already unloaded
      }
      await Audio.setAudioModeAsync({ allowsRecordingIOS: false, playsInSilentModeIOS: true }).catch(() => {});
    }
  }, []);

  const cancel = useCallback(() => {
    gen.current++;
    setState('idle');
    void release();
  }, [release]);

  useEffect(() => () => cancel(), [cancel]);

  const start = useCallback(async () => {
    const my = ++gen.current;
    setError(null);
    try {
      if (Platform.OS === 'web') {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (my !== gen.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        const mr = new MediaRecorder(stream);
        const chunks: Blob[] = [];
        mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
        mr.start();
        web.current = { mr, chunks, stream };
      } else {
        const perm = await Audio.requestPermissionsAsync();
        if (!perm.granted) throw new Error('Microphone permission denied');
        await Audio.setAudioModeAsync({ allowsRecordingIOS: true, playsInSilentModeIOS: true });
        const { recording } = await Audio.Recording.createAsync(Audio.RecordingOptionsPresets.HIGH_QUALITY);
        if (my !== gen.current) {
          await recording.stopAndUnloadAsync().catch(() => {});
          await Audio.setAudioModeAsync({ allowsRecordingIOS: false, playsInSilentModeIOS: true }).catch(() => {});
          return;
        }
        rec.current = recording;
      }
      setState('recording');
    } catch (e) {
      if (my !== gen.current) return;
      setError(e instanceof Error ? e.message : 'Could not start the microphone');
      setState('idle');
    }
  }, []);

  const stop = useCallback(async () => {
    const my = gen.current;
    setState('transcribing');
    try {
      const form = new FormData();
      if (Platform.OS === 'web') {
        const w = web.current;
        web.current = null;
        if (!w) throw new Error('Not recording');
        const blob = await new Promise<Blob>((resolve) => {
          w.mr.onstop = () => resolve(new Blob(w.chunks, { type: w.mr.mimeType || 'audio/webm' }));
          w.mr.stop();
        });
        w.stream.getTracks().forEach((t) => t.stop());
        form.append('audio', blob, 'speech.webm');
      } else {
        const r = rec.current;
        rec.current = null;
        if (!r) throw new Error('Not recording');
        await r.stopAndUnloadAsync();
        await Audio.setAudioModeAsync({ allowsRecordingIOS: false, playsInSilentModeIOS: true });
        const uri = r.getURI();
        if (!uri) throw new Error('No recording');
        form.append('audio', { uri, name: 'speech.m4a', type: 'audio/m4a' } as unknown as Blob);
      }
      const text = await transcribe(form);
      if (my === gen.current && text) onText(text);
    } catch (e) {
      if (my === gen.current) setError(e instanceof Error ? e.message : 'Transcription failed');
    } finally {
      if (my === gen.current) setState('idle');
    }
  }, [onText]);

  return { state, error, start, stop, cancel, toggle: state === 'recording' ? stop : start };
}
